import { Box, Button, Divider, Flex, Grid, GridItem, Text, VStack } from "@chakra-ui/react";
import { CheckIcon, CopyIcon, RepeatIcon } from "@chakra-ui/icons";
import { useMemo } from "react";
import {
  CHANGE_TYPE_ADD,
  CHANGE_TYPE_REMOVE,
  formatChangesForClipboard,
  StagedChange,
} from "../../domain/feature-flags";
import { CustomerMap } from "../../domain/customer";
import { FeatureFlag } from "../../api/feature-flags";
import useMetriportToast from "../shared/toast";
import { InlineChangesetEditor } from "./InlineChangesetEditor";

export type ChangesetPanelProps = {
  changes: StagedChange[];
  customerMap: CustomerMap;
  featureFlags: Record<string, FeatureFlag>;
  onToggleChange: (change: StagedChange) => void;
  onApply: () => void;
  onClear: () => void;
  isApplying?: boolean;
};

type GroupedChanges = Record<string, Array<{ cxId: string; customerName: string }>>;

export function ChangesetPanel({
  changes,
  customerMap,
  featureFlags,
  onToggleChange,
  onApply,
  onClear,
  isApplying = false,
}: ChangesetPanelProps) {
  const toast = useMetriportToast();

  function getCustomerName(cxId: string): string {
    const customer = customerMap[cxId];
    return customer?.rootCustomer.email ?? cxId;
  }

  function copyToClipboard() {
    const text = formatChangesForClipboard(changes);
    navigator.clipboard.writeText(text);
    toast.success({ title: "Copied to clipboard" });
  }

  // Group changes by flag name
  const groupedAdditions = useMemo(() => {
    const grouped: GroupedChanges = {};
    changes
      .filter(c => c.type === CHANGE_TYPE_ADD)
      .forEach(change => {
        if (!grouped[change.flagName]) {
          grouped[change.flagName] = [];
        }
        grouped[change.flagName].push({
          cxId: change.cxId,
          customerName: getCustomerName(change.cxId),
        });
      });
    return grouped;
  }, [changes, customerMap]);

  const groupedRemovals = useMemo(() => {
    const grouped: GroupedChanges = {};
    changes
      .filter(c => c.type === CHANGE_TYPE_REMOVE)
      .forEach(change => {
        if (!grouped[change.flagName]) {
          grouped[change.flagName] = [];
        }
        grouped[change.flagName].push({
          cxId: change.cxId,
          customerName: getCustomerName(change.cxId),
        });
      });
    return grouped;
  }, [changes, customerMap]);

  const hasChanges = changes.length > 0;
  const additionCount = changes.filter(c => c.type === CHANGE_TYPE_ADD).length;
  const removalCount = changes.filter(c => c.type === CHANGE_TYPE_REMOVE).length;
  const additionEntries = Object.entries(groupedAdditions);
  const removalEntries = Object.entries(groupedRemovals);

  return (
    <Box
      bg="white"
      borderRadius="md"
      borderWidth="1px"
      borderColor="gray.200"
      p={4}
      mb={6}
      w="full"
    >
      {/* Header with actions */}
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Text fontWeight="bold" fontSize="lg">
          Changeset
        </Text>
        <Flex gap={2}>
          <Button
            size="sm"
            variant="outline"
            leftIcon={<RepeatIcon />}
            onClick={onClear}
            isDisabled={!hasChanges}
          >
            Reset
          </Button>
          <Button
            size="sm"
            colorScheme="green"
            leftIcon={<CheckIcon />}
            onClick={onApply}
            isLoading={isApplying}
            isDisabled={!hasChanges}
          >
            Apply
          </Button>
        </Flex>
      </Flex>

      {/* Side-by-side layout: Editor on left, Preview on right */}
      <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={4}>
        {/* Left: Inline editor */}
        <GridItem>
          <InlineChangesetEditor
            featureFlags={featureFlags}
            customerMap={customerMap}
            changes={changes}
            onToggleChange={onToggleChange}
          />
        </GridItem>

        {/* Right: Pending changes preview */}
        <GridItem>
          <Flex justifyContent="space-between" alignItems="center" mb={2}>
            <Text fontSize="sm" fontWeight="medium" color="gray.600">
              Pending Changes
            </Text>
            {hasChanges && (
              <Button
                size="xs"
                variant="ghost"
                leftIcon={<CopyIcon />}
                onClick={copyToClipboard}
                color="gray.500"
                _hover={{ color: "gray.700" }}
              >
                Copy diff
              </Button>
            )}
          </Flex>

          <Box
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="md"
            bg="gray.50"
            minH="280px"
            maxH="280px"
            overflowY="auto"
          >
            {hasChanges ? (
              <Box>
                {additionCount > 0 && (
                  <Box>
                    <Box
                      px={3}
                      py={1.5}
                      bg="green.50"
                      borderBottomWidth="1px"
                      borderColor="gray.200"
                      position="sticky"
                      top={0}
                    >
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        color="green.600"
                        textTransform="uppercase"
                      >
                        Adding ({additionCount})
                      </Text>
                    </Box>
                    <VStack align="stretch" gap={0}>
                      {additionEntries.map(([flagName, customers]) => (
                        <Box key={flagName} px={3} py={1}>
                          <Text fontSize="xs" fontWeight="medium" color="gray.700">
                            {flagName}
                          </Text>
                          {customers.map(({ cxId, customerName }) => (
                            <Flex key={cxId} fontSize="xs" color="green.600" ml={2} gap={1}>
                              <Text>+ {customerName}</Text>
                              <Text color="gray.400" fontFamily="mono" fontSize="2xs">
                                {cxId}
                              </Text>
                            </Flex>
                          ))}
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}

                {additionCount > 0 && removalCount > 0 && <Divider borderColor="gray.300" />}

                {removalCount > 0 && (
                  <Box>
                    <Box
                      px={3}
                      py={1.5}
                      bg="red.50"
                      borderBottomWidth="1px"
                      borderColor="gray.200"
                      position="sticky"
                      top={0}
                    >
                      <Text
                        fontSize="xs"
                        fontWeight="semibold"
                        color="red.600"
                        textTransform="uppercase"
                      >
                        Removing ({removalCount})
                      </Text>
                    </Box>
                    <VStack align="stretch" gap={0}>
                      {removalEntries.map(([flagName, customers]) => (
                        <Box key={flagName} px={3} py={1}>
                          <Text fontSize="xs" fontWeight="medium" color="gray.700">
                            {flagName}
                          </Text>
                          {customers.map(({ cxId, customerName }) => (
                            <Flex key={cxId} fontSize="xs" color="red.600" ml={2} gap={1}>
                              <Text>− {customerName}</Text>
                              <Text color="gray.400" fontFamily="mono" fontSize="2xs">
                                {cxId}
                              </Text>
                            </Flex>
                          ))}
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}
              </Box>
            ) : (
              <Flex h="full" minH="280px" alignItems="center" justifyContent="center" p={4}>
                <Text fontSize="sm" color="gray.500" textAlign="center">
                  No pending changes.
                  <br />
                  Select a flag and toggle customers to build your changeset.
                </Text>
              </Flex>
            )}
          </Box>
        </GridItem>
      </Grid>
    </Box>
  );
}
